import { PLAYER_RESULTS } from '../../../shared/play-report'
import { HEARTBEAT_MS } from '../../../shared/heartbeat'

export default function playerComponent ($log, $interval, ClientService, PlayerFactory, PlayerApiService, PlayerUtil, PrimusService, OfflineScheduleService) {
  'ngInject'
  return {
    restrict: 'E',
    scope: {
      scene: '='
    },
    bindToController: {},
    controller: () => { },
    controllerAs: 'ctrl',
    template: '<div id="stageWrapper"><div id="stage"></div></div>',
    link: function (scope, elem) {
      // If authentication failed or this Client isn't configured for this scene,
      // show an informative error screen and don't do anything further.
      if (scope.scene.error || ClientService.id !== scope.scene.clientId) {
        const stageWrapper = angular.element(elem[0].querySelector('#stageWrapper'))
        stageWrapper.css({
          'background-color': '#000000',
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
          width: '100vw',
          height: '100vh'
        })
        const stage = angular.element(elem[0].querySelector('#stage'))
        stage.css({
          color: '#ffffff',
          'font-family': 'sans-serif',
          'text-align': 'center',
          padding: '2em'
        })
        const sceneName = angular.element('<span>').text(scope.scene.name || 'Unknown').html()
        stage.html(
          '<h1 style="font-size: 2.5em; margin-bottom: 0.5em;">&#9888; Not Configured</h1>' +
          '<p style="font-size: 1.3em; opacity: 0.8;">This player is not set up to display this scene:</p>' +
          '<p style="font-size: 1.8em; font-weight: bold;">' + sceneName + '</p>'
        )

        // This player is not the assigned one, so it does nothing further: no
        // scene subscription (player endpoints require an assigned session) and
        // no polling. When this browser becomes the assigned player it must be
        // refreshed manually to start playing. The previously active player is
        // kicked automatically by its own scene watcher (in the assigned-player
        // path below) the moment the scene is unassigned from it.
        return
      }

      if ('serviceWorker' in navigator) {
        window.addEventListener('beforeunload', () => {
          // Send a message to the service worker to signal a page refresh
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'PAGE_REFRESH'
            })
          }
        })
        // The service worker self-activates (skipWaiting + clients.claim on
        // install), so a new version takes control on its own and fires
        // 'controllerchange'. Reload off that single event rather than the old
        // per-install statechange reload, which could fire repeatedly and loop.
        // Guards: `refreshing` caps it at one reload per page load; the first
        // claim of a page that loaded without a controller (first visit) is
        // adopted silently — its code is already current, so no reload needed.
        let refreshing = false
        let controlled = !!navigator.serviceWorker.controller
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return
          if (!controlled) {
            controlled = true
            return
          }
          refreshing = true
          $log.info('[Player] New service worker took control. Reloading once.')
          window.location.reload()
        })

        navigator.serviceWorker.register('/service-worker.js')
          .then(function (registration) {
            $log.info('Service Worker registered with scope:', registration.scope)

            // Check for updates in the background; a new worker found here
            // activates itself and triggers the controllerchange reload above.
            registration.update()
          })
          .catch(function (error) {
            $log.info('Service Worker registration failed:', error)
          })
      }

      // Setup stage
      const stageWrapper = angular.element(elem[0].querySelector('#stageWrapper'))
      PlayerUtil.setOrientationClass(scope.scene.orientation, stageWrapper)
      const stage = angular.element(elem[0].querySelector('#stage'))

      // RF2026: Persistent animated background overlay (lives in stageWrapper, not stage,
      // so stage.html('') never removes it and the animation runs uninterrupted)
      const rf2026Bg = angular.element(
        '<div id="rf2026-bg">' +
          '<div class="rf2026-gradient"></div>' +
          '<div class="rf2026-blur"></div>' +
        '</div>'
      )
      stageWrapper.append(rf2026Bg)

      // Offline status indicator — small red dot at bottom center, only visible when disconnected
      const offlineDot = angular.element('<div></div>')
      offlineDot.css({
        position: 'fixed',
        bottom: '12px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '10px',
        height: '10px',
        'border-radius': '50%',
        'background-color': '#e53e3e',
        opacity: '0.85',
        'z-index': '99999',
        display: 'none',
        'pointer-events': 'none'
      })
      stageWrapper.append(offlineDot)

      const unsubscribeStatus = PrimusService.onConnectionStatusChange(status => {
        offlineDot.css('display', status !== 'connected' ? 'block' : 'none')
      })

      // Init Offline Schedule Sync
      OfflineScheduleService.startSync(scope.scene.id)

      // When connection is lost and regained, re-authenticate the player session
      // and re-subscribe to playlist changes before reloading the play queue.
      PrimusService.on('reconnected', () => {
        $log.warn(`[Player] Primus reconnected: Re-authenticating for scene id ${scope.scene.id}`)
        PlayerApiService.authenticate(scope.scene.id)
          .then(() => PlayerApiService.subscribe(handlePlayQueueChange))
          .then(() => {
            clearPlayQueue()
            getPlayQueueForPlayback()
            consecutiveOfflineFailures = 0
            // Re-validate offline cache (collection may have changed while we were offline)
            OfflineScheduleService.onReconnect()
          })
          .catch(err => {
            $log.error(`[Player] Reconnect re-authentication failed for scene id ${scope.scene.id}`, err)
          })
      })

      // On disconnect, ensure we are ready to switch to offline mode
      // (The actual switch happens in playNext when queue runs dry)
      PrimusService.on('reconnect failed', () => {
        $log.warn('[Player] Connection lost. Will switch to Offline Schedule when current queue is empty.')
      })

      /// Player logic

      let playing = null
      const playQueue = {}

      // NB: playQueue has circular structure, as the child scopes reference their parent.
      function getPlayQueueForLogging () {
        const logQueue = {}
        for (const [key, value] of Object.entries(playQueue)) {
          logQueue[key] = {
            id: value.id,
            ready: !!value.ready,
            data: value.scope.data
          }
        }

        return logQueue
      }

      function getPlayQueueForPlayback () {
        PlayerApiService.getPlayQueue()
          .then(playlistItems => {
            for (const playlistItem of playlistItems) {
              handlePlayQueueChange(null, playlistItem)
            }
          })
          .catch(err => {
            $log.error(`[Player] Could not get current playQueue for scene id ${scope.scene.id}`, err)
          })
      }

      function cleanupPlayer (playerObj) {
        playerObj.scope.$destroy()
        if (playing && playing.id === playerObj.id) {
          playing = null
        }
      }

      function clearPlayQueue () {
        for (const key in playQueue) {
          if (Object.prototype.hasOwnProperty.call(playQueue, key)) {
            playQueue[key].scope.$destroy()
            delete playQueue[key]
          }
        }
      }

      function playNext () {
        if (playing) {
          // Stall check
          if (playing.api.hasPlayed()) {
            $log.warn(`[Player] playNext: playing hasPlayed, forcing cleanup for scene id ${scope.scene.id}:`, playing)
            cleanupPlayer(playing)
          } else {
            return
          }
        }

        // Find the next ready item to play.
        // Real items (numeric IDs) are prioritised over offline items (string IDs).
        const allIds = Object.keys(playQueue)
        const realIds = allIds.filter(id => !isNaN(id)).map(Number).sort((a, b) => a - b)
        const offlineIds = allIds.filter(id => isNaN(id))
        const playlistItemIds = [...realIds, ...offlineIds]

        let idToPlay = null
        for (const playlistItemId of playlistItemIds) {
          if (playQueue[playlistItemId].ready) {
            idToPlay = playlistItemId
            break
          } else {
            $log.warn(`[Player] playNext: Skipping ${playlistItemId}, NOT ready!`)
          }
        }

        if (!idToPlay) {
          // Check for offline fallback
          const connectionStatus = PrimusService.getConnectionStatus()
          if (connectionStatus !== 'connected') {
            const offlineItem = OfflineScheduleService.getNextItem()
            if (offlineItem) {
              $log.info('[Player] Offline Mode: Scheduling item from local schedule', offlineItem)
              // Offline items already have the flat playData shape — feed straight into PlayerFactory
              const player = PlayerFactory.create(offlineItem, scope.scene.orientation, scope)
              if (player) {
                playQueue[offlineItem.id] = player
              } else {
                $log.error('[Player] Offline Mode: Could not create player for offline item', offlineItem)
              }
              return
            } else {
              $log.warn('[Player] Offline Mode: No items in Offline Schedule!')
            }
          }

          $log.warn('[Player] playNext: Nothing ready to be played', getPlayQueueForLogging())
          return
        }

        // Setup for playing!
        playing = playQueue[idToPlay]
        delete playQueue[idToPlay]
        stage.html('')
        stage.append(playing.elem)

        // RF2026: Toggle animated background for social/serviceAnnouncement content
        const rf2026Types = ['social', 'serviceAnnouncement']
        if (rf2026Types.includes(playing.scope.data.type)) {
          stageWrapper.addClass('rf2026-active')
        } else {
          stageWrapper.removeClass('rf2026-active')
        }

        $log.log(`[Player] playNext: playing item with id ${playing.id} of type ${playing.scope.data.type}`, getPlayQueueForLogging())

        playing.api.play()

        const isOffline = playing.scope.data._isOfflineItem

        // Track active collection for cache invalidation (must happen before caching
        // so the cache is cleared before new items from a different collection are added)
        if (playing.scope.data.sceneCollectionId) {
          OfflineScheduleService.notifyActiveCollection(playing.scope.data.sceneCollectionId)
        }

        // Cache the item for offline playback (only real items, not ones we generated)
        OfflineScheduleService.cachePlayedItem(playing.scope.data, isOffline)

        // Don't call backend for offline-sourced items
        if (!isOffline) {
          PlayerApiService.playingStarted(idToPlay, PLAYER_RESULTS.STARTED)
          PlayerApiService.setCurrentlyPlaying(idToPlay)
        }
      }

      let playNextInterval = $interval(playNext, 1000)

      // Emitted when a player is ready to play
      scope.$on('preloadComplete', function (event, id) {
        $log.log(`[Player] preloadComplete of item ${id} for scene id ${scope.scene.id}`)
        if (playQueue[id]) {
          // Q: How can we get a preloadComplete if the item is not on the queue?
          // A1: Item already destroyed, but listeners not removed
          // A2: video can get 'canplaythrough' multiple times for the same video
          playQueue[id].ready = true
          consecutiveOfflineFailures = 0
          // If nothing is playing, start immediately to avoid black-screen gap
          if (!playing) {
            playNext()
          }
        }
      })

      // Emitted when a player has successfully played
      scope.$on('playingEnded', function (event, id) {
        if (playing && id === playing.id) {
          $log.log(`[Player] playingEnded of item ${id} for scene id ${scope.scene.id}`)
          const isOffline = playing.scope.data._isOfflineItem
          if (!isOffline) {
            PlayerApiService.playingEnded(id, PLAYER_RESULTS.ENDED)
          }
          cleanupPlayer(playing)
          playNext()
        } else {
          $log.error(`[Player] playingEnded of item ${id} for scene id ${scope.scene.id}, but this item wasn't playing! Playing id is: ${playing && playing.id}`)
        }
      })

      // Emitted when a player encountered an error
      let consecutiveOfflineFailures = 0

      scope.$on('playerError', function (event, id, error) {
        if (playing && playing.id === id) {
          // Error on playing: Log, cleanup and trigger next
          $log.error(`[Player] playerError of item ${id} for scene id ${scope.scene.id} AS PLAYING. Error: ${error.message}`, error)
          const isOffline = playing.scope.data._isOfflineItem
          cleanupPlayer(playing)
          if (isOffline) {
            consecutiveOfflineFailures++
          }
          playNext()
        } else if (playQueue[id]) {
          // Error on queue: Log, cleanup and trigger next
          const erroredPlayer = playQueue[id]
          $log.error(`[Player] playerError of item ${id} (${erroredPlayer.scope.data.type}) for scene id ${scope.scene.id} on queue. Error: ${error.message}`, error)
          const isOffline = erroredPlayer.scope.data._isOfflineItem
          // Destroy and remove the failed player to prevent queue bloat
          erroredPlayer.scope.$destroy()
          delete playQueue[id]
          if (!isOffline) {
            const truncatedMsg = (error.message || '').substring(0, 200)
            PlayerApiService.playingEnded(id, `Error: ${truncatedMsg}`)
              .finally(() => {
                playNext()
              })
          } else {
            consecutiveOfflineFailures++
            if (consecutiveOfflineFailures >= 3) {
              $log.warn(`[Player] ${consecutiveOfflineFailures} consecutive offline failures. Pausing offline attempts.`)
              return // Stop — wait for reconnect or next interval tick
            }
            playNext()
          }
        }
      })

      /// PlayQueue change handling

      // Take care of incoming playlistItems
      function handlePlayQueueChange (err, playlistItem) {
        if (err) {
          $log.error(`[Player] handlePlayQueueChange got error for scene id ${scope.scene.id}:`, err)
          return
        }

        // The change was the removal of a playlistItem
        if (playlistItem.removed) {
          // We only remove stuff if on the queue. We don't interrupt the playing item.
          if (playQueue[playlistItem.id]) {
            playQueue[playlistItem.id].scope.$destroy()
            delete playQueue[playlistItem.id]
            $log.warn(`[Player] handlePlayQueueChange removed unplayed for scene id ${scope.scene.id}:`, playlistItem)
          }

          return
        }

        /// Otherwise the change was the addition of a new playlistItem

        // Parse the extra data on playlistItem
        if (typeof playlistItem.itemData === 'string') {
          try {
            playlistItem.itemData = JSON.parse(playlistItem.itemData)
          } catch (e) {
            $log.error(`[Player] handlePlayQueueChange got new playlistItem, but could not parse itemData for scene id ${scope.scene.id}:`, playlistItem)
            PlayerApiService.playingEnded(playlistItem.id, `Could not parse itemData: ${e.message}`)
            return undefined
          }
        }

        // Do the shuffle
        const itemData = playlistItem.itemData
        delete playlistItem.itemData
        Object.assign(playlistItem, itemData)

        // Build player
        const player = PlayerFactory.create(playlistItem, scope.scene.orientation, scope)
        if (player) {
          playQueue[playlistItem.id] = player
        } else {
          $log.error(`[Player] handlePlayQueueChange got new playlistItem, but could not create player for scene id ${scope.scene.id}:`, playlistItem)
          PlayerApiService.playingEnded(playlistItem.id, 'Could not build player')
          return undefined
        }
      }

      // Get currently queued playlistItems
      getPlayQueueForPlayback()

      // Watch this player's scene: the moment it is unassigned (clientId no
      // longer ours) reload, which drops to the wrong-scene screen. Only the
      // assigned player establishes this backend subscription (player/subscribe
      // below, gated by checkPlayerAuth), so non-assigned browsers never receive
      // these events. Per-item currentlyPlaying updates keep clientId unchanged,
      // so they don't trigger a reload. A newly assigned player is started by a
      // manual refresh, not by this watcher.
      PlayerApiService.subscribeToScene(function (err, scene) {
        if (err || !scene) return
        // Scene deletes broadcast { id, removed } with no clientId — ignore.
        if (typeof scene.clientId === 'undefined') return
        if (scene.clientId !== ClientService.id) {
          $log.info(`[Player] Scene ${scope.scene.id} unassigned from this player. Reloading.`)
          window.location.reload()
        }
      })

      // Setup subscription for playQueue changes
      PlayerApiService.subscribe(handlePlayQueueChange)
        .catch(err => {
          $log.error(`[Player] Could not subscribe to playQueue changes for scene id ${scope.scene.id}`, err)
        })

      /// Scene status

      // Set version
      PlayerApiService.heartbeat(JSON.stringify(ClientService.version))

      // Send heartbeat and setup interval
      function sendHeartbeat () {
        PlayerApiService.heartbeat()
      }
      sendHeartbeat()
      let heartbeatInterval = $interval(sendHeartbeat, HEARTBEAT_MS)

      scope.$on('$destroy', () => {
        // Cleanup status indicator
        if (unsubscribeStatus) {
          unsubscribeStatus()
        }

        // Cleanup heartbeat
        if (heartbeatInterval) {
          $interval.cancel(heartbeatInterval)
          heartbeatInterval = undefined
        }

        // Cleanup playNext
        if (playNextInterval) {
          $interval.cancel(playNextInterval)
          playNextInterval = undefined
        }
      })
    }
  }
}
